import React from 'react';
import { Check, X } from 'lucide-react';

interface PasswordStrengthIndicatorProps {
  password: string;
}

interface ValidationRule {
  label: string;
  test: (password: string) => boolean;
}

const validationRules: ValidationRule[] = [
  {
    label: 'Almeno 8 caratteri',
    test: (pwd) => pwd.length >= 8,
  },
  {
    label: 'Una lettera maiuscola',
    test: (pwd) => /[A-Z]/.test(pwd),
  },
  {
    label: 'Una lettera minuscola',
    test: (pwd) => /[a-z]/.test(pwd),
  },
  {
    label: 'Un numero',
    test: (pwd) => /[0-9]/.test(pwd),
  },
  {
    label: 'Un carattere speciale (!@#$%^&*)',
    test: (pwd) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
  },
];

export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
}) => {
  const passedRules = validationRules.filter((rule) => rule.test(password));
  const strength = passedRules.length;
  const strengthPercentage = (strength / validationRules.length) * 100;

  const getStrengthColor = () => {
    if (strength <= 2) return 'bg-red-500';
    if (strength <= 3) return 'bg-yellow-500';
    if (strength <= 4) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getStrengthLabel = () => {
    if (strength <= 2) return 'Debole';
    if (strength <= 3) return 'Media';
    if (strength <= 4) return 'Forte';
    return 'Molto Forte';
  };

  if (!password) return null;

  return (
    <div className="space-y-3">
      {/* Barra di forza */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-gray-700">Forza password</span>
          <span className="text-sm font-semibold text-gray-900">{getStrengthLabel()}</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${getStrengthColor()}`}
            style={{ width: `${strengthPercentage}%` }}
          />
        </div>
      </div>

      {/* Requisiti */}
      <div className="space-y-1">
        {validationRules.map((rule, index) => {
          const passed = rule.test(password);
          return (
            <div key={index} className="flex items-center gap-2">
              {passed ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <X className="w-4 h-4 text-gray-400" />
              )}
              <span className={`text-sm ${passed ? 'text-green-700' : 'text-gray-600'}`}>
                {rule.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const validatePassword = (password: string): boolean => {
  return validationRules.every((rule) => rule.test(password));
};
